import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { BookOpen, Plus, CheckCircle2, Clock, XCircle, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { formatUKDate, formatUKLongDate } from "@/lib/dateUtils";

interface ClientHomeworkProps {
  clientId: string;
  clinicianId: string;
}

const HOMEWORK_TYPES = [
  { value: 'worksheet', label: 'Worksheet' },
  { value: 'reflection', label: 'Reflection' },
  { value: 'behavioural', label: 'Behavioural Task' },
  { value: 'cognitive', label: 'Cognitive Exercise' },
  { value: 'mindfulness', label: 'Mindfulness Practice' },
  { value: 'journaling', label: 'Journaling' },
  { value: 'reading', label: 'Reading' },
  { value: 'custom', label: 'Custom' }
];

export function ClientHomework({ clientId, clinicianId }: ClientHomeworkProps) {
  const queryClient = useQueryClient();
  const [showAddForm, setShowAddForm] = useState(false);
  const [newHomework, setNewHomework] = useState({
    title: '',
    description: '',
    homework_type: 'reflection',
    due_date: '',
    priority: 'medium'
  });
  const [feedbackId, setFeedbackId] = useState<string | null>(null);
  const [feedback, setFeedback] = useState('');

  // Fetch homework
  const { data: homework, isLoading } = useQuery({
    queryKey: ['client-homework', clientId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('client_homework')
        .select('*')
        .eq('client_id', clientId)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data;
    }
  });

  // Add homework
  const addMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('client_homework')
        .insert({
          client_id: clientId,
          clinician_id: clinicianId,
          ...newHomework,
          due_date: newHomework.due_date || null
        });
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-homework', clientId] });
      setShowAddForm(false);
      setNewHomework({
        title: '',
        description: '',
        homework_type: 'reflection',
        due_date: '',
        priority: 'medium'
      });
      toast.success("Homework assigned successfully");
    },
    onError: () => {
      toast.error("Failed to assign homework");
    }
  });

  // Add feedback
  const feedbackMutation = useMutation({
    mutationFn: async ({ id, feedback }: { id: string; feedback: string }) => {
      const { error } = await supabase
        .from('client_homework')
        .update({
          clinician_feedback: feedback,
          status: 'reviewed',
          reviewed_at: new Date().toISOString()
        })
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-homework', clientId] });
      setFeedbackId(null);
      setFeedback('');
      toast.success("Feedback added");
    }
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
      case 'reviewed':
        return <CheckCircle2 className="h-4 w-4 text-green-600" />;
      case 'in_progress':
        return <Clock className="h-4 w-4 text-yellow-600" />;
      case 'cancelled':
        return <XCircle className="h-4 w-4 text-red-600" />;
      default:
        return <Clock className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-500/20 text-green-700 border-green-500/30">Completed</Badge>;
      case 'reviewed':
        return <Badge className="bg-blue-500/20 text-blue-700 border-blue-500/30">Reviewed</Badge>;
      case 'in_progress':
        return <Badge className="bg-yellow-500/20 text-yellow-700 border-yellow-500/30">In Progress</Badge>;
      case 'cancelled':
        return <Badge className="bg-red-500/20 text-red-700 border-red-500/30">Cancelled</Badge>;
      default:
        return <Badge variant="outline">Assigned</Badge>;
    }
  };

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case 'high':
        return <Badge className="bg-red-500/20 text-red-700 border-red-500/30">High Priority</Badge>;
      case 'low':
        return <Badge className="bg-muted text-muted-foreground">Low Priority</Badge>;
      default:
        return null;
    }
  };

  return (
    <Card className="bg-card/50 backdrop-blur-sm border-border/30">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-primary" />
            Therapeutic Homework
          </CardTitle>
          <Dialog open={showAddForm} onOpenChange={setShowAddForm}>
            <DialogTrigger asChild>
              <Button size="sm">
                <Plus className="h-4 w-4 mr-1" />
                Assign Homework
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Assign New Homework</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Title</label>
                  <Input
                    value={newHomework.title}
                    onChange={(e) => setNewHomework({ ...newHomework, title: e.target.value })}
                    placeholder="e.g., Thought Record Exercise"
                    className="bg-background/50"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Description</label>
                  <Textarea
                    value={newHomework.description}
                    onChange={(e) => setNewHomework({ ...newHomework, description: e.target.value })}
                    placeholder="Detailed instructions for the client..."
                    className="bg-background/50 min-h-24"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Type</label>
                    <Select
                      value={newHomework.homework_type}
                      onValueChange={(value) => setNewHomework({ ...newHomework, homework_type: value })}
                    >
                      <SelectTrigger className="bg-background/50">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {HOMEWORK_TYPES.map(type => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">Priority</label>
                    <Select
                      value={newHomework.priority}
                      onValueChange={(value) => setNewHomework({ ...newHomework, priority: value })}
                    >
                      <SelectTrigger className="bg-background/50">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Due Date (Optional)</label>
                  <Input
                    type="date"
                    value={newHomework.due_date}
                    onChange={(e) => setNewHomework({ ...newHomework, due_date: e.target.value })}
                    className="bg-background/50"
                  />
                </div>

                <div className="flex gap-2 pt-2">
                  <Button variant="outline" onClick={() => setShowAddForm(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={() => addMutation.mutate()}
                    disabled={!newHomework.title || !newHomework.description || addMutation.isPending}
                  >
                    Assign Homework
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <p className="text-muted-foreground text-sm">Loading homework...</p>
        ) : homework?.length === 0 ? (
          <p className="text-muted-foreground text-sm">No homework assigned yet</p>
        ) : (
          homework?.map(item => (
            <div
              key={item.id}
              className="p-4 rounded-lg bg-muted/30 border border-border/30 space-y-3"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  {getStatusIcon(item.status)}
                  <h4 className="font-medium">{item.title}</h4>
                </div>
                <div className="flex items-center gap-2">
                  {getPriorityBadge(item.priority)}
                  {getStatusBadge(item.status)}
                </div>
              </div>

              <p className="text-sm text-muted-foreground">{item.description}</p>

              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                <span>Assigned: {formatUKDate(new Date(item.created_at))}</span>
                {item.due_date && <span>Due: {formatUKDate(new Date(item.due_date))}</span>}
                <Badge variant="outline" className="text-xs">
                  {HOMEWORK_TYPES.find(t => t.value === item.homework_type)?.label}
                </Badge>
              </div>

              {item.client_reflection && (
                <div className="p-3 rounded bg-primary/10 border border-primary/20">
                  <p className="text-xs font-medium text-primary mb-1">Client Reflection:</p>
                  <p className="text-sm">{item.client_reflection}</p>
                </div>
              )}

              {item.clinician_feedback && (
                <div className="p-3 rounded bg-accent/20 border border-accent/30">
                  <p className="text-xs font-medium mb-1">Your Feedback:</p>
                  <p className="text-sm">{item.clinician_feedback}</p>
                </div>
              )}

              {item.status === 'completed' && !item.clinician_feedback && (
                <div className="space-y-2">
                  {feedbackId === item.id ? (
                    <div className="space-y-2">
                      <Textarea
                        value={feedback}
                        onChange={(e) => setFeedback(e.target.value)}
                        placeholder="Provide feedback on this homework..."
                        className="bg-background/50"
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setFeedbackId(null);
                            setFeedback('');
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => feedbackMutation.mutate({ id: item.id, feedback })}
                          disabled={!feedback || feedbackMutation.isPending}
                        >
                          Submit Feedback
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setFeedbackId(item.id)}
                    >
                      <MessageSquare className="h-4 w-4 mr-1" />
                      Add Feedback
                    </Button>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}
